import { useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Crown, Play, ShoppingCart } from "lucide-react";
import Layout from "@/components/Layout";
import CourseModuleRow from "@/components/CourseModuleRow";
import type { Course, CourseModule, CourseModuleGroup } from "@/types/course";
import { GET_COURSE_BY_SLUG } from "@/queries/courses";
import { extractYouTubeId } from "@/lib/youtube";
import { useMyDataQuery } from "@/generated/graphql";
import { deriveCoursePurchaseState } from "@/lib/courses/deriveCoursePurchaseState";
import { toast } from "@/hooks/use-toast";
import { consumeRememberedViralizePurchase } from "@/lib/analytics/trackViralizePurchase";

const PURCHASE_POLL_INTERVAL_MS = 3000;
const PURCHASE_POLL_MAX_ATTEMPTS = 20;

function formatCoursePrice(price: number): string {
  return `R$ ${price.toFixed(2).replace(".", ",")}`;
}

function findNextTarget(course: Course): CourseModule | null {
  const allModules: CourseModule[] = [
    ...course.ungroupedModules,
    ...course.moduleGroups.flatMap((g) => g.modules),
  ];
  // First non-completed accessible module (free or unlocked)
  const next = allModules.find((m) => !m.isCompleted && !m.isLocked);
  if (next) return next;
  // Fall back to the first module so the user always has somewhere to go
  return allModules[0] ?? null;
}

function groupProgress(group: CourseModuleGroup): { done: number; total: number } {
  const total = group.modules.length;
  const done = group.modules.filter((m) => m.isCompleted).length;
  return { done, total };
}

const CoursePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en" : "pt-BR";
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading, error, refetch } = useQuery<{ course: Course | null }>(GET_COURSE_BY_SLUG, {
    variables: { slug: slug ?? "", locale },
    skip: !slug,
  });

  const { data: myData, loading: myDataLoading } = useMyDataQuery();

  const course = data?.course ?? null;
  const courseId = course?.id ?? null;
  const ownedByViewer = course?.ownedByViewer ?? false;
  const purchaseSuccessParam = searchParams.get("purchase") === "success";

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef(0);
  const hasHandledOwnedRef = useRef(false);

  useEffect(() => {
    if (!purchaseSuccessParam || !courseId) {
      return;
    }

    if (ownedByViewer) {
      if (!hasHandledOwnedRef.current) {
        hasHandledOwnedRef.current = true;
        consumeRememberedViralizePurchase();
        toast({
          title: t("coursePurchase.confirmedTitle"),
          description: t("coursePurchase.confirmedBody"),
        });
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("purchase");
            return next;
          },
          { replace: true }
        );
      }
      return;
    }

    // Not owned yet — poll the course query until the webhook lands, or give up.
    pollAttemptsRef.current = 0;
    pollTimerRef.current = setInterval(() => {
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > PURCHASE_POLL_MAX_ATTEMPTS) {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        return;
      }
      refetch();
    }, PURCHASE_POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [purchaseSuccessParam, courseId, ownedByViewer, refetch, setSearchParams, t]);

  const myDataReady = !myDataLoading && !!myData;
  const isAdmin = myDataReady ? myData?.myData?.is_admin === true : false;

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto text-center text-muted-foreground py-12">
            {t("common.loading")}
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !course) {
    return (
      <Layout>
        <div className="flex-1 p-4 lg:p-6">
          <div className="container mx-auto max-w-2xl text-center py-12 space-y-4">
            <p className="text-lg font-semibold">{t("course.notFound")}</p>
            <Link to="/tutorials">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("course.back")}
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const purchaseCta = myDataReady
    ? deriveCoursePurchaseState({
        isPurchasable: course.isPurchasable,
        price: course.price,
        ownedByViewer: course.ownedByViewer,
        isAdmin,
      })
    : { kind: "hidden" as const };
  const showBuyCta = purchaseCta.kind === "buy";
  const buyCtaLabel = purchaseCta.kind === "buy"
    ? t("coursePurchase.buyCta", { price: formatCoursePrice(purchaseCta.price) })
    : "";

  const allModules: CourseModule[] = [
    ...course.ungroupedModules,
    ...course.moduleGroups.flatMap((g) => g.modules),
  ];
  const freeModule = allModules.find((m) => m.isFree && m.videoUrl);
  const videoId = freeModule && freeModule.videoUrl
    ? extractYouTubeId(freeModule.videoUrl)
    : null;
  const thumbnail = course.thumbnailUrl
    || freeModule?.thumbnailUrl
    || (videoId
      ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
      : "/images/autoclipper-logo-full.png");

  const progress = course.progressPercent;
  const isCourseComplete = progress === 100;
  const completedAny = progress > 0;
  const nextTarget = !isCourseComplete ? findNextTarget(course) : null;

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        <div className="container mx-auto max-w-5xl space-y-6">
          {/* Back link */}
          <Link
            to="/tutorials"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("course.back")}
          </Link>

          {/* Hero — two-column on lg+ */}
          <div className="rounded-2xl border border-primary/40 bg-card overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="relative lg:w-[360px] lg:flex-shrink-0 bg-black overflow-hidden">
                <img
                  src={thumbnail}
                  alt={course.title}
                  className="w-full aspect-video lg:aspect-auto lg:h-full lg:absolute lg:inset-0 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-card/90" />
                {isCourseComplete && (
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-primary text-primary-foreground border-0 font-semibold">
                      {t("course.courseCompleted")}
                    </Badge>
                  </div>
                )}
              </div>
              <div className="flex-1 p-5 lg:p-6 space-y-4">
                <div className="space-y-1.5">
                  <h1 className="text-2xl lg:text-3xl font-bold leading-tight">
                    {course.title}
                  </h1>
                  {course.description && (
                    <p className="text-sm text-muted-foreground">{course.description}</p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    <Crown className="w-3 h-3 mr-1" />
                    {course.weeksLabel || t("course.weeksLabel")}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Play className="w-3 h-3 mr-1" />
                    {t("course.modulesCount", { count: course.moduleCount })}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {course.durationLabel || t("course.duration")}
                  </Badge>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">
                      {progress > 0
                        ? t("course.progressLabel", { percent: progress })
                        : t("course.pilotUnlocked")}
                    </span>
                    <span className="text-primary font-semibold">{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-[width] duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {(nextTarget || showBuyCta) && (
                  <div className="pt-1 flex flex-wrap items-center gap-2">
                    {nextTarget && (
                      <Link to={`/courses/${course.slug}/${nextTarget.order}`}>
                        <Button className="bg-gradient-primary text-primary-foreground font-semibold hover:opacity-90 border-0">
                          {completedAny ? t("course.continueLesson") : t("course.startNow")}
                        </Button>
                      </Link>
                    )}
                    {showBuyCta && (
                      <Link to={`/courses/${course.slug}/checkout`}>
                        <Button variant="outline" className="font-semibold">
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          {buyCtaLabel}
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Compact buy banner above the module list */}
          {showBuyCta && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3">
              <p className="text-sm text-foreground">{t("coursePurchase.lockedBanner")}</p>
              <Link to={`/courses/${course.slug}/checkout`}>
                <Button size="sm" variant="outline" className="font-semibold whitespace-nowrap">
                  <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                  {buyCtaLabel}
                </Button>
              </Link>
            </div>
          )}

          {/* Ungrouped modules (no card, simple list) */}
          {course.ungroupedModules.length > 0 && (
            <div className="space-y-2">
              {course.ungroupedModules.map((module) => (
                <Link
                  key={module.id}
                  to={`/courses/${course.slug}/${module.order}`}
                  className="block"
                >
                  <CourseModuleRow module={module} onClick={() => {}} />
                </Link>
              ))}
            </div>
          )}

          {/* Group cards */}
          {course.moduleGroups.map((group) => {
            const { done, total } = groupProgress(group);
            const groupPercent = total === 0 ? 0 : Math.round((done / total) * 100);
            return (
              <div
                key={group.id}
                className="rounded-2xl border border-border bg-card p-4 lg:p-5 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {group.order}
                    </span>
                    <h2 className="text-base font-semibold truncate">{group.label}</h2>
                  </div>
                  <span className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                    {t("course.completedCount", { done, total })}
                  </span>
                </div>
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-[width] duration-300"
                    style={{ width: `${groupPercent}%` }}
                  />
                </div>
                <div className="space-y-2 pt-1">
                  {group.modules.map((module) => (
                    <Link
                      key={module.id}
                      to={`/courses/${course.slug}/${module.order}`}
                      className="block"
                    >
                      <CourseModuleRow module={module} onClick={() => {}} />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default CoursePage;
